import React, { useEffect, useState } from 'react';
import { Button, Row, Col } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faEdit, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

//Components
import MainCard from '../../../../../components/MainCard';
import TextField from '../../../../../components/TextField';

//Api
import regraVersaoSistema_api from '../../../../../api/regraVersaoSistema_api';

//Helpers
import { showMessage } from '../../../../../helpers/message';
import { validarCampos, objCampo } from '../../../../../helpers/ValidadorDeCampos';
import { downloadZip } from '../../../../../helpers/downloadFile';
import { getSessionCookie } from '../../../../../helpers/cookies';
import { opcoesCategoria, CATEGORIA_REGRA, validarArquivoPdf } from '../helpers/regrasEVersoes';

//Context
import { useAuth } from '../../../../../context/auth';

function initialStateRegraVersao(secretaria_Id, categoriaPadrao) {
    return {
        titulo: "",
        descricao: "",
        dataPublicacao: moment().format("YYYY-MM-DD"),
        categoria: categoriaPadrao ?? CATEGORIA_REGRA,
        arquivo: "",
        status: "Ativo",
        secretaria_Id: secretaria_Id
    }
}

export default function CadastroRegraVersao({ id, btnVoltar, editavel = false, categoriaPadrao }) {
    //Dados de autenticação
    const session = getSessionCookie();

    //Estado de controle dos campos de erro
    const [errors, setErrors] = useState({});

    //Estado de controle dos campos de cadastro
    const [regraVersao, setRegraVersao] = useState(initialStateRegraVersao(session.secretaria_Id, categoriaPadrao));

    //PDF selecionado para envio (File do input)
    const [arquivoUpload, setArquivoUpload] = useState(null);

    const { setLoading } = useAuth();

    //Estado de controle dos campos obrigatórios
    const campos = [
        objCampo({ name: "titulo", required: true }),
        objCampo({ name: "descricao", required: true }),
        objCampo({ name: "dataPublicacao", required: true }),
        objCampo({ name: "categoria", required: true }),
        objCampo({ name: "status", required: true })
    ];

    //Função de load do registro para visualização/atualização
    const loadRegraVersao = (id = null) => {
        if (id == null)
            return;

        setLoading(true);
        regraVersaoSistema_api.getItem(id).then(response => {
            response.data.dataPublicacao = moment(response.data.dataPublicacao).format("YYYY-MM-DD");
            setRegraVersao(response.data);
        })
            .catch(e => showMessage("Erro", e.message, "error", null))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        loadRegraVersao(id);
    }, []);

    //Função de onChange dos campos de cadastro
    const onChange = (e) => {
        const { name, value } = e.target;
        const _regraVersao = {
            ...regraVersao,
            [name]: value
        };

        //Validando o campo específico para checar em tempo de digitação se ele possui algum erro
        const _campos = campos.filter(campo => campo.name === name);
        const fieldError = validarCampos(_regraVersao, _campos);

        const _errors = { ...errors };
        delete _errors[name];
        if (fieldError[1].length === 0)
            setErrors({ ..._errors });
        else
            setErrors({ ..._errors, ...fieldError[0] });

        setRegraVersao(_regraVersao);
    }

    //Função de onChange do arquivo PDF — guarda o File cru, sem converter para base64
    const onChangeAnexo = (e) => {
        const arquivo = e.target.files?.[0] ?? null;
        const erro = validarArquivoPdf(arquivo);

        if (erro) {
            e.target.value = "";
            setArquivoUpload(null);
            showMessage("Atenção", erro, "warning", null);
            return;
        }

        setArquivoUpload(arquivo);
    }

    //Função para dar submit nos dados
    const onSubmit = () => {
        //Realizando a validação dos campos obrigatórios
        const _errors = validarCampos(regraVersao, campos);
        setErrors(_errors[0]);

        if (_errors[1].length > 0)
            return;

        setLoading(true);
        const _regraVersao = { ...regraVersao };

        const requisicao = _regraVersao.id
            ? regraVersaoSistema_api.put(_regraVersao, arquivoUpload)
            : regraVersaoSistema_api.post(_regraVersao, arquivoUpload);

        requisicao
            .then(() => showMessage("Sucesso", "Operação realizada com sucesso", "success", btnVoltar))
            .catch(err => showMessage("Erro", err?.message ?? "Erro na operação.", "error", null))
            .finally(() => setLoading(false));
    }

    //O arquivo é gravado zipado; só há download depois de salvo (quando existe linkArquivo).
    const possuiArquivoSalvo = !!regraVersao.linkArquivo;

    const formatarDataHora = (dataHora) =>
        dataHora ? moment(dataHora).format("DD/MM/YYYY HH:mm") : "";

    return (
        <>
            <Button onClick={btnVoltar} className='px-4'><FontAwesomeIcon icon={faAngleLeft} className='me-2' /> Voltar</Button>
            <MainCard>
                <Row className='d-flex align-items-center justify-content-between mb-4'>
                    <Col md="12" lg="12" className='text-start'>
                        <h5 className='text-secondary'>{id ? "Regra / Versão do Sistema" : "Adicionar Regra / Versão do Sistema"}</h5>
                    </Col>
                </Row>
                <Row>
                    <Col md="12" lg="4">
                        <TextField
                            label="* Título"
                            name={"titulo"}
                            onChange={onChange}
                            value={regraVersao.titulo}
                            invalid={!!errors.titulo}
                            maxLength={200}
                            disabled={!editavel}
                        />
                    </Col>
                    <Col md="12" lg="2">
                        <TextField
                            type={"select"}
                            label="* Status"
                            name={"status"}
                            options={[
                                { label: "Ativo", value: "Ativo" },
                                { label: "Inativo", value: "Inativo" }
                            ]}
                            onChange={onChange}
                            value={regraVersao.status}
                            invalid={!!errors.status}
                            disabled={!editavel}
                        />
                    </Col>
                    <Col md="12" lg="3">
                        <TextField
                            type={"select"}
                            label="* Categoria"
                            name={"categoria"}
                            options={opcoesCategoria}
                            onChange={onChange}
                            value={regraVersao.categoria}
                            invalid={!!errors.categoria}
                            disabled={!editavel}
                        />
                    </Col>
                    <Col md="12" lg="3">
                        <TextField
                            type={"date"}
                            label="* Data da Publicação"
                            name={"dataPublicacao"}
                            onChange={onChange}
                            value={regraVersao.dataPublicacao}
                            invalid={!!errors.dataPublicacao}
                            disabled={!editavel}
                        />
                    </Col>
                    <Col md="12" lg="12">
                        <TextField
                            label="* Descrição"
                            name={"descricao"}
                            type={"textarea"}
                            onChange={onChange}
                            value={regraVersao.descricao}
                            invalid={!!errors.descricao}
                            disabled={!editavel}
                        />
                    </Col>
                    <Col md="12" lg={possuiArquivoSalvo ? "9" : "12"}>
                        <TextField
                            label="Arquivo PDF"
                            name={"arquivo"}
                            type={"file"}
                            onChange={onChangeAnexo}
                            invalid={!!errors.arquivo}
                            accept="application/pdf"
                            disabled={!editavel}
                        />
                    </Col>
                    {possuiArquivoSalvo &&
                        <Col md="12" lg="3">
                            <Button
                                className='w-100 mt-2'
                                color='danger'
                                onClick={() => downloadZip(regraVersao.arquivo, regraVersao.linkArquivo)}>
                                <FontAwesomeIcon icon={faFilePdf} /> Download PDF
                            </Button>
                        </Col>
                    }
                </Row>
                {regraVersao.id &&
                    <Row className='mt-3 border-top pt-3'>
                        <Col md="12" lg="12">
                            <h6 className='text-secondary'>Histórico</h6>
                        </Col>
                        <Col md="12" lg="6">
                            <small className='text-secondary'>
                                <strong>Publicado por:</strong> {regraVersao.usuarioPublicacao} em {formatarDataHora(regraVersao.dataHoraPublicacao)}
                            </small>
                        </Col>
                        <Col md="12" lg="6">
                            <small className='text-secondary'>
                                <strong>Última atualização:</strong> {regraVersao.dataHoraAtualizacao
                                    ? `${regraVersao.usuarioAtualizacao} em ${formatarDataHora(regraVersao.dataHoraAtualizacao)}`
                                    : "Sem atualizações"}
                            </small>
                        </Col>
                    </Row>
                }
                <Row>
                    <Col md="12" lg="12" className={`d-flex align-items-center justify-content-center mt-4 ${editavel ? "" : "d-none"}`}>
                        <Button
                            className='px-4'
                            color={id ? 'warning text-white' : 'success'}
                            onClick={onSubmit}
                        >
                            {id ? <><FontAwesomeIcon icon={faEdit} /> Atualizar</> : "+ Cadastrar"}
                        </Button>
                    </Col>
                </Row>
            </MainCard>
        </>
    );
}
